import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ImprovementPlanActionFormDto, ImprovementPlanFormDto, ImprovementPlanObjectiveDto } from "../features/improvementPlan/types";
import { ObjectiveFinalStatusEnum } from "../features/improvementPlan/types/enums";

export interface ImprovementPlanState {
    data: ImprovementPlanFormDto;
    proposedActionsPage: boolean;
    errors: {
        [key: string]: string | undefined;
    };
}

const initialState: ImprovementPlanState = {
    data: {
        id: 0,
        creationDate: new Date().toISOString(),
        responsibleId: undefined,
        title: "",
        description: "",
        startDate: undefined,
        expectedEndDate: undefined,
        endDate: undefined,
        status: 0,
        estimatedInverstment: undefined,
        returnedInverstment: undefined,
        originId: undefined,
        typeId: undefined,
        objectives: [],
        selectedFunctionalAreas: [],
        selectedServices: [],
        actions: [],
        demands: [],
        files: []
    },
    proposedActionsPage: false,
    errors: {}
};

const improvementPlanSlice = createSlice({
    name: "improvementPlanForm",
    initialState,
    reducers: {
        setForm(state, action: PayloadAction<Partial<ImprovementPlanFormDto>>) {
            Object.assign(state.data, action.payload);
        },
        setField(
            state,
            action: PayloadAction<{ field: keyof ImprovementPlanFormDto; value: ImprovementPlanFormDto[keyof ImprovementPlanFormDto] }>
        ) {
            const { field, value } = action.payload;
            state.data[field] = value as never;
        },
        setError(state, action: PayloadAction<{ field: keyof ImprovementPlanFormDto | string; message: string | undefined }>) {
            const { field, message } = action.payload;
            state.errors[field] = message;
        },
        setErrors(state, action: PayloadAction<{ [key: string]: string | undefined }>) {
            state.errors = action.payload;
        },
        clearErrors(state) {
            state.errors = {};
        },
        addObjective(state) {
            state.data.objectives.push({ id: 0, description: "", finalStatus: undefined });
        },
        updateObjectiveDescription(
            state,
            action: PayloadAction<{
                index: number;
                description: string;
                finalStatus?: ObjectiveFinalStatusEnum;
            }>
        ) {
            const { index, description, finalStatus } = action.payload;
            const objectives = state.data.objectives ?? [];
            if (!objectives[index]) return;
            objectives[index] = {
                ...objectives[index],
                description,
                ...(finalStatus !== undefined && { finalStatus })
            };
        },
        removeObjective(state, action: PayloadAction<number>) {
            state.data.objectives = (state.data.objectives ?? []).filter((_, i) => i !== action.payload);
        },
        updateObjectives(state, action: PayloadAction<ImprovementPlanObjectiveDto[]>) {
            state.data.objectives = action.payload;
        },
        addImprovementActions(state, action: PayloadAction<{ actions: ImprovementPlanActionFormDto[]; }>) {
            const { actions } = action.payload;

            const currentItems = state.data.actions ?? [];
            state.data.actions = [...currentItems, ...actions];
        },
        resetForm() {
            return initialState;
        },
        setProposedActionsPage(state, action: PayloadAction<{ proposedActionsPage: boolean; }>) {
            const { proposedActionsPage } = action.payload;

            state.proposedActionsPage = proposedActionsPage;
        },
    }
});

export const {
    setForm,
    setField,
    setError,
    setErrors,
    clearErrors,
    addObjective,
    updateObjectiveDescription,
    removeObjective,
    updateObjectives,
    addImprovementActions,
    resetForm,
    setProposedActionsPage
} = improvementPlanSlice.actions;

export default improvementPlanSlice.reducer;
